import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, Clock, MapPin, Send, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { contactInfo } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PageHero({ eyebrow, title, subtitle }) {
  return (
    <section className="relative pt-[76px] bg-[#F4F7FB] diag-lines">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <span className="eyebrow">{eyebrow}</span>
        <h1 className="font-display text-5xl md:text-6xl text-brand mt-3">{title}</h1>
        {subtitle && (
          <p className="text-slate-600 text-lg mt-4 max-w-2xl">{subtitle}</p>
        )}
      </div>
    </section>
  );
}

const contactCards = [
  { icon: Phone, label: "Call Us", value: contactInfo.phone, href: `tel:${contactInfo.phone.replace(/[^0-9]/g, "")}` },
  { icon: Mail, label: "Email Us", value: contactInfo.email, href: `mailto:${contactInfo.email}` },
  { icon: Clock, label: "Hours", value: contactInfo.hours },
  { icon: MapPin, label: "Location", value: contactInfo.address },
];

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({
        title: "Message sent!",
        description:
          "Thanks for reaching out — our support team will get back to you shortly.",
      });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description:
          "We couldn't send your message right now. Please try again or call us directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <PageHero
        eyebrow="We're Here To Help"
        title="Contact Us"
        subtitle="Have questions about ELDT, our courses, or getting started? Our in-house support team is standing by."
      />

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10">
          {/* Info */}
          <div className="lg:col-span-2 space-y-5">
            {contactCards.map((c) => {
              const Inner = (
                <div className="card-lift bg-white border border-slate-200 rounded-2xl p-6 flex items-start gap-4 soft-shadow">
                  <div className="w-12 h-12 rounded-lg bg-[#16365C]/8 flex items-center justify-center shrink-0">
                    <c.icon className="w-6 h-6 text-steel" />
                  </div>
                  <div>
                    <div className="font-head font-semibold uppercase tracking-wide text-slate-400 text-xs">
                      {c.label}
                    </div>
                    <div className="text-slate-900 text-lg mt-1">{c.value}</div>
                  </div>
                </div>
              );
              return c.href ? (
                <a key={c.label} href={c.href} className="block">
                  {Inner}
                </a>
              ) : (
                <div key={c.label}>{Inner}</div>
              );
            })}
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-slate-200 rounded-2xl p-8 soft-shadow">
              <h2 className="font-display text-3xl text-brand mb-6">
                Send Us A Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-slate-700">Full Name</Label>
                    <Input
                      id="name" name="name" required value={form.name} onChange={handleChange}
                      placeholder="John Driver"
                      className="mt-2 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-brand"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-700">Phone</Label>
                    <Input
                      id="phone" name="phone" value={form.phone} onChange={handleChange}
                      placeholder="(555) 000-0000"
                      className="mt-2 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-brand"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email" name="email" type="email" required value={form.email} onChange={handleChange}
                    placeholder="you@example.com"
                    className="mt-2 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-brand"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-slate-700">Message</Label>
                  <Textarea
                    id="message" name="message" required rows={5} value={form.message} onChange={handleChange}
                    placeholder="How can we help you get on the road?"
                    className="mt-2 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-brand resize-none"
                  />
                </div>
                <button type="submit" disabled={submitting} className="btn-brand rounded-md px-8 py-3.5 inline-flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
                  {submitting ? (
                    <>
                      Sending <Loader2 className="w-4 h-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      Send Message <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
